import React, { useState } from 'react';
import {
  Database,
  Cloud,
  Upload,
  X,
  Info,
  CheckCircle,
  Zap,
  Globe,
  Shield,
  DollarSign
} from 'lucide-react';
import VercelBlobUploader from './VercelBlobUploader';
import QuickProductAdmin from './QuickProductAdmin';

const StorageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [showUploader, setShowUploader] = useState(false);

  const storageOptions = [
    {
      id: 'vercel-blob',
      name: 'Vercel Blob',
      icon: Cloud,
      color: 'green',
      recommended: true,
      description: 'Modern cloud storage with global CDN',
      features: [
        { icon: Globe, text: 'Global CDN with 100+ edge locations' },
        { icon: Zap, text: 'Zero cold starts & instant serving' },
        { icon: DollarSign, text: '5GB free + pay-per-use pricing' },
        { icon: Shield, text: 'Enterprise security & 99.9% uptime' }
      ],
      pros: [
        'Faster global delivery',
        'Automatic image optimization',
        'Native Vercel integration',
        'Cost-effective pricing'
      ],
      cons: [
        'Newer technology',
        'Requires Vercel account'
      ]
    },
    {
      id: 'supabase',
      name: 'Supabase Storage',
      icon: Database,
      color: 'blue',
      recommended: false,
      description: 'Traditional database storage (legacy)',
      features: [
        { icon: Database, text: 'PostgreSQL-based storage' },
        { icon: Shield, text: 'Reliable and stable' },
        { icon: Upload, text: 'Direct file uploads' },
        { icon: CheckCircle, text: 'Proven technology' }
      ],
      pros: [
        'Stable and reliable',
        'Existing setup',
        'Direct database integration',
        'Familiar interface'
      ],
      cons: [
        'Slower global delivery',
        'Higher bandwidth costs',
        'No automatic optimization'
      ]
    }
  ];

  const handleStorageSelect = (storageId) => {
    setSelectedStorage(storageId);
    setShowUploader(true);
    setIsOpen(false);
  };

  const handleClose = () => {
    setShowUploader(false);
    setSelectedStorage(null);
  };

  if (!isOpen) {
    return (
      <>
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 bg-gradient-to-r from-blue-600 to-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 group"
          title="Upload Products"
        >
          <Upload size={24} className="group-hover:scale-110 transition-transform" />
        </button>

        {showUploader && selectedStorage === 'vercel-blob' && (
          <VercelBlobUploader
            onClose={handleClose}
            onUploadComplete={(results) => {
              console.log('Vercel Blob upload completed:', results);
              handleClose();
              window.location.reload();
            }}
          />
        )}

        {showUploader && selectedStorage === 'supabase' && (
          <QuickProductAdmin />
        )}
      </>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-green-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Upload className="mr-3" size={28} />
              Choose Storage Provider
            </h2>
            <p className="text-gray-600 mt-1">Select where to upload your product images</p>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Storage Options */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {storageOptions.map((storage) => {
              const IconComponent = storage.icon;
              const isRecommended = storage.recommended;
              
              return (
                <div
                  key={storage.id}
                  className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                    isRecommended 
                      ? 'border-green-200 bg-green-50 hover:border-green-300' 
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                  }`}
                  onClick={() => handleStorageSelect(storage.id)}
                >
                  {/* Recommended Badge */}
                  {isRecommended && (
                    <div className="absolute -top-3 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      ⭐ RECOMMENDED
                    </div>
                  )}

                  {/* Header */}
                  <div className="flex items-center mb-4">
                    <div className={`p-3 rounded-lg ${
                      storage.color === 'green' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                    }`}>
                      <IconComponent size={24} />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-semibold text-gray-900">{storage.name}</h3>
                      <p className="text-gray-600 text-sm">{storage.description}</p>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {storage.features.map((feature, index) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div key={index} className="flex items-center text-sm text-gray-700">
                          <FeatureIcon size={16} className={`mr-2 ${
                            storage.color === 'green' ? 'text-green-600' : 'text-blue-600'
                          }`} />
                          {feature.text}
                        </div>
                      );
                    })}
                  </div>

                  {/* Pros and Cons */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <h4 className="font-medium text-green-800 mb-2 text-sm">✅ Advantages:</h4>
                      <ul className="space-y-1">
                        {storage.pros.map((pro, index) => (
                          <li key={index} className="text-xs text-gray-600">• {pro}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-orange-800 mb-2 text-sm">⚠️ Considerations:</h4>
                      <ul className="space-y-1">
                        {storage.cons.map((con, index) => (
                          <li key={index} className="text-xs text-gray-600">• {con}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Action Button */}
                  <button
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                      storage.color === 'green'
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Upload to {storage.name}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Info Section */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <Info className="h-6 w-6 text-blue-600 mr-3 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Hybrid Storage System</h3>
                <div className="text-sm text-blue-800 space-y-2">
                  <p>
                    <strong>🔄 Seamless Integration:</strong> Both storage options work together. 
                    Your customers will see all products regardless of where they're stored.
                  </p>
                  <p>
                    <strong>📊 Smart Management:</strong> Firebase handles metadata for both storage providers, 
                    ensuring consistent product information and search functionality.
                  </p>
                  <p>
                    <strong>🚀 Future-Ready:</strong> Start with Vercel Blob for new uploads to benefit from 
                    global CDN delivery while keeping existing Supabase images working perfectly.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">100+</div>
              <div className="text-sm text-green-800">Global Edge Locations</div>
              <div className="text-xs text-green-600">Vercel Blob CDN</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">99.9%</div>
              <div className="text-sm text-blue-800">Uptime Guarantee</div>
              <div className="text-xs text-blue-600">Both Providers</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">5GB</div>
              <div className="text-sm text-purple-800">Free Storage</div>
              <div className="text-xs text-purple-600">Vercel Blob Tier</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageSelector;
